/**
 * Price indexes that can be derived from retail price 1. Price 1 is always the source of truth.
 */
export const MULTIPLE_PRICE_CHANGE_PRICE_INDEXES: readonly number[] = [2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * How the GP for a derived price is resolved.
 * - `fixed`: the price always targets a set GP percentage.
 * - `variable`: the price targets price 1 GP plus/minus an offset.
 * - `none`: the price is left untouched.
 */
export type TMultiplePriceGpMode = 'fixed' | 'variable' | 'none';

/**
 * Comparison applied to the chosen GP percentage to decide which branch of a rule runs.
 */
export type TMultiplePriceConditionOperator =
  'always'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'equalTo';

/**
 * GP percentage a condition is measured against.
 * - `priceOneGp`: the GP the item achieves on retail price 1.
 * - `targetGp`: the item's Target GP % (`recommendedGP`) from IQ.
 */
export type TMultiplePriceConditionSource = 'priceOneGp' | 'targetGp';

export type TMultiplePriceChangeRulesSource = 'store' | 'default';

export interface IMultiplePriceGpAction {
  mode: TMultiplePriceGpMode;
  priceBanding: boolean;
  value: number;
}

export interface IMultiplePriceCondition {
  operator: TMultiplePriceConditionOperator;
  source: TMultiplePriceConditionSource;
  value: number;
}

/**
 * Rule for a single derived price (2-10).
 */
export interface IMultiplePriceChangeRule {
  condition: IMultiplePriceCondition;
  enabled: boolean;
  priceIndex: number;
  whenFalse: IMultiplePriceGpAction;
  whenTrue: IMultiplePriceGpAction;
}

/**
 * Store-scoped multiple price change rules used by Feature → Stock Settings and the stock manager.
 */
export interface IMultiplePriceChangeRulesStoreSettings {
  enabled: boolean;
  rules: IMultiplePriceChangeRule[];
  source: TMultiplePriceChangeRulesSource;
}

/**
 * Firestore document shape: rules keyed by price index so a single price can be patched in isolation.
 */
export interface IMultiplePriceChangeRulesDocument {
  enabled: boolean;
  rules: Record<string, Omit<IMultiplePriceChangeRule, 'priceIndex'>>;
  updatedAt?: number;
}

/**
 * Outcome of applying one rule to a stock item, used for the stock manager preview modal.
 */
export interface IMultiplePriceChangeResult {
  conditionMet: boolean;
  effectiveGp: number;
  exclusive: number;
  exclusiveField: string;
  inclusive: number;
  inclusiveField: string;
  mode: TMultiplePriceGpMode;
  previousInclusive: number;
  priceBandingApplied: boolean;
  priceIndex: number;
  targetGp: number;
}
